import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AccessCase, AccessRequest, SessionAccess } from '../models/organizationAccess';
import { AuthenticationService, Session } from './authentication.service';

/** One-shot flag kept across the reload that follows an access ended by the backend: the organisation left. */
const ACCESS_ENDED_KEY = 'uzai-access-ended';

/**
 * Organisation access ("passage") of a platform team member inside a client organisation.
 *
 * Every change of organisation or role (enter, change role, leave, access ended) stores the new session and
 * then reloads the whole page: several services and the role/ACL directives keep what they computed for the
 * previous session and are never reset, so an in-app navigation would mix two organisations.
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationAccessService {

  /**
   * Leaving goes around the interceptors: the route needs no access token, and its failure must never start a
   * refresh, a logout or another leave.
   */
  private readonly bareHttp: HttpClient;

  /** Only one leave in flight, whoever asked for it (banner or interceptor). */
  private leaving = false;

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient,
    httpBackend: HttpBackend
  ) {
    this.bareHttp = new HttpClient(httpBackend);
  }

  /** The access the session acts under, if any. */
  current(): SessionAccess | undefined {
    return this.authService.currentUser()?.access;
  }

  /**
   * Enters a client organisation, or switches role or organisation from inside an access (the backend closes
   * the one in progress). On success the application reloads at `returnUrl` (an in-app path), else at the
   * client's home, and never returns.
   */
  async enter(request: AccessRequest, returnUrl?: string): Promise<void> {
    const session = await firstValueFrom(
      this.http.post<Session>(`${environment.backend}/auth/access`, this.buildRequest(request))
    );
    this.authService.replaceSession(session);
    this.reload(this.isAppPath(returnUrl) ? returnUrl : '/');
  }

  /** Leaves on request: back to the admin dashboard of the organisation just left. */
  async leave(): Promise<void> {
    if (this.leaving) return;
    this.leaving = true;
    const access = this.current();
    try {
      await this.backToPlatform();
    } catch (err) {
      this.leaving = false;
      throw err;
    }
    this.reload(access ? `/admin/organizations/${access.organization.id}` : '/admin');
  }

  /**
   * The backend answered ACCESS_ENDED (expired, member suspended or demoted, client suspended): back to the
   * platform organisation, with a notice shown after the reload. If even that fails, a plain logout.
   */
  ended(): void {
    if (this.leaving) return;
    this.leaving = true;
    const organization = this.current()?.organization.name ?? '';
    this.backToPlatform().then(() => {
      try {
        sessionStorage.setItem(ACCESS_ENDED_KEY, organization);
      } catch {
        // The notice is a convenience: without storage, the reload still happens.
      }
      this.reload('/admin');
    }, () => {
      this.authService.logout();
    });
  }

  /** The organisation of an access that has just ended, once: the flag is cleared when read. */
  takeEndedNotice(): string | undefined {
    try {
      const organization = sessionStorage.getItem(ACCESS_ENDED_KEY);
      if (organization === null) return undefined;
      sessionStorage.removeItem(ACCESS_ENDED_KEY);
      return organization;
    } catch {
      return undefined;
    }
  }

  /** The newest cases of a client organisation (50 at most), for the Case Operator scope. */
  searchCases(organizationId: string, query?: string): Promise<AccessCase[]> {
    let params = new HttpParams();
    if (query && query.trim().length > 0) {
      params = params.set('q', query.trim());
    }
    return firstValueFrom(this.http.get<AccessCase[]>(
      `${environment.backend}/auth/access/organizations/${organizationId}/cases`, { params }
    ));
  }

  /** The body of POST /auth/access: no empty reason, a constraint only for the roles that take one. */
  private buildRequest(request: AccessRequest): AccessRequest {
    const reason = request.reason?.trim();
    return {
      organizationId: request.organizationId,
      role: request.role,
      ...(reason ? { reason } : {}),
      ...(request.constraint ? { constraint: request.constraint } : {}),
    };
  }

  private async backToPlatform(): Promise<void> {
    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      throw new Error('NO_REFRESH_TOKEN');
    }
    const session = await firstValueFrom(
      this.bareHttp.post<Session>(`${environment.backend}/auth/access/leave`, { refreshToken })
    );
    this.authService.replaceSession(session);
  }

  // Only a path of this application: never another origin
  private isAppPath(path?: string): path is string {
    return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\');
  }

  private reload(path: string): void {
    window.location.assign(path);
  }
}
